import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import axios from 'src/axios';
import PasswordInput from 'src/components/UI/input/PasswordInput';
import TextInput from '../UI/input/TextInput';
import { UserContext } from 'src/store/ContextStore';
import {
  APICONSTANTS,
  CONSTANTS,
  RECAPTCHA_SITE_KEY
} from 'src/components/Constants';
import logo from 'src/assets/images/trakgeneLogo.png';
import { injectGoogleCaptchaScript, validateEmail } from 'src/util/util';
import useSystemDefaultTheme from 'src/hooks/useSystemDefaultTheme';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export const StyledPaper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(8),
  marginInline: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

export const FormContainer = styled('form')(({ theme }) => ({
  width: '40%',
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

export const Logo = styled('div')(({ theme }) => ({
  width: '40%',
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

const StudentRegister = () => {
  const t = useAutoTranslation();

  const navigate = useNavigate();
  // Anonymous page: use the System Admin's appearance (TGCLOUD-1844 QA).
  useSystemDefaultTheme();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState({ status: false });

  const isValid = () => !!email && !!password && !!confirmPassword;

  const recaptcha = React.useRef('');

  React.useEffect(() => {
    axios
      .get(APICONSTANTS.recaptcha)
      .then((res) => {
        injectGoogleCaptchaScript(null, res.data.siteKey);
        recaptcha.current = res.data.siteKey;
      })
      .catch((err) => console.error(err));
  }, []);

  //func to verify email and pass
  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (alert.status) setAlert((prev) => ({ ...prev, status: false }));

    if (!validateEmail(email)) {
      setAlert({ status: true, severity: 'error', msg: t('Invalid email!') });
      return;
    }
    if (password.length < 6) {
      setAlert({
        status: true,
        severity: 'error',
        msg: t('Please enter a secure password')
      });
      return;
    }
    if (password !== confirmPassword) {
      setAlert({
        status: true,
        severity: 'error',
        msg: t("Password doesn't match")
      });
      return;
    }

    window.grecaptcha.ready(function () {
      try {
        window.grecaptcha.execute(recaptcha.current).then(function (grToken) {
          axios
            .post('/login/register/student', { email, password, grToken })
            .then((res) => {
              setLoading(false);
              // A staff or student account already holds the address
              // (TGCLOUD-1853; patients hold no login since TGCLOUD-1926).
              if (res.data.exists) {
                setAlert({
                  status: true,
                  severity: 'error',
                  msg: t(
                    'An account already exists for this email address. Sign in, or register with a different email address.'
                  )
                });
                return;
              }
              if (res.data.status) navigate(CONSTANTS.signinPath);
            })
            .catch((err) => {
              setLoading(false);
              let error = err?.response?.data?.error ?? t('An error occurred');
              setAlert({
                status: true,
                severity: 'error',
                msg: error
              });
              setPassword('');
            });
        });
      } catch (err) {
        setAlert({
          status: true,
          severity: 'error',
          msg: t('Bot Detected')
        });
        setPassword('');
      }
    });
  };

  return (
    <StyledPaper>
      <Logo>
        <img style={{ width: '100%' }} src={logo} alt={t('Trakgene Logo')} />
      </Logo>
      <FormContainer className={`user-form`} onSubmit={onSubmit}>
        <Grid
          container
          spacing={3}
          sx={{
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {alert.status && (
            <Grid size={12}>
              <Alert variant="filled" severity={alert.severity}>
                {alert.msg}
              </Alert>
            </Grid>
          )}
          <Grid size={12}>
            <TextInput
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="email"
            />
          </Grid>

          <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <Grid container spacing={2}>
              <Grid size={12}>
                <PasswordInput
                  ariaLabel={t('enter password')}
                  label={t('Password')}
                  name="newPassword"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={12}>
                <PasswordInput
                  ariaLabel={t('enter password')}
                  label={t('Confirm Password')}
                  name="newPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </Grid>

              <Grid size={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={loading || !isValid()}
                  endIcon={loading ? <CircularProgress size={15} /> : undefined}
                  fullWidth
                  onSubmit={onSubmit}
                >
                  {t('Register')}
                </Button>
              </Grid>

              <Grid size={12}>
                {t('Already have an account?')}
                <Link to={CONSTANTS.signinPath}>
                  <strong>{t('Sign in')}</strong>
                </Link>
              </Grid>
            </Grid>
          </Slide>
        </Grid>
      </FormContainer>
    </StyledPaper>
  );
};

export default StudentRegister;
